import * as React from "react"

import { cn } from "../../../lib/utils"
import { Button } from "../button/button"
import { Icon } from "../icon/icon"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../popover/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../tooltip/tooltip"
import { Toggle } from "../toggle/toggle"

export type ReactionActorPreview = {
  id: string
  content: React.ReactNode
}

export type ReactionBarItem = {
  id: string
  symbol: React.ReactNode
  count: number
  pressed: boolean
  pending?: boolean
  disabled?: boolean
  addLabel: string
  removeLabel: string
  actorPreviewLabel?: string
  actors?: readonly ReactionActorPreview[]
  moreActorsContent?: React.ReactNode
}

export type ReactionBarProps = Omit<
  React.ComponentProps<"div">,
  "children" | "onChange"
> & {
  items: readonly ReactionBarItem[]
  addPickerLabel: string
  onPressedChange: (id: string, pressed: boolean) => void
}

function ReactionBar({
  items,
  addPickerLabel,
  onPressedChange,
  className,
  ...props
}: ReactionBarProps) {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false)
  const [isAddTooltipOpen, setIsAddTooltipOpen] = React.useState(false)
  const [focusedPickerIndex, setFocusedPickerIndex] = React.useState(-1)
  const pickerButtonRefs = React.useRef<Array<HTMLButtonElement | null>>([])
  const isAddTooltipSuppressed = React.useRef(false)

  const isPickerItemDisabled = (item: ReactionBarItem) =>
    Boolean(item.disabled || item.pending || item.pressed)

  const findEnabledPickerIndexes = () =>
    items.flatMap((item, index) =>
      isPickerItemDisabled(item) ? [] : [index],
    )

  const handlePickerOpenChange = (open: boolean) => {
    setIsPickerOpen(open)
    if (!open) return
    setIsAddTooltipOpen(false)
    setFocusedPickerIndex(findEnabledPickerIndexes()[0] ?? -1)
  }

  const handlePickerSelection = (item: ReactionBarItem) => {
    onPressedChange(item.id, true)
    isAddTooltipSuppressed.current = true
    setIsAddTooltipOpen(false)
    setIsPickerOpen(false)
  }

  const handlePickerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) => {
    const enabledIndexes = findEnabledPickerIndexes()
    if (enabledIndexes.length === 0) return
    const currentPosition = enabledIndexes.indexOf(currentIndex)
    let nextIndex: number | undefined

    if (event.key === "Home") nextIndex = enabledIndexes[0]
    if (event.key === "End") nextIndex = enabledIndexes.at(-1)
    if (event.key === "ArrowRight") {
      nextIndex =
        enabledIndexes[(currentPosition + 1) % enabledIndexes.length]
    }
    if (event.key === "ArrowLeft") {
      nextIndex =
        enabledIndexes[
          (currentPosition - 1 + enabledIndexes.length) %
            enabledIndexes.length
        ]
    }
    if (nextIndex === undefined) return

    event.preventDefault()
    setFocusedPickerIndex(nextIndex)
    pickerButtonRefs.current[nextIndex]?.focus()
  }

  const hasAvailablePickerItem = findEnabledPickerIndexes().length > 0

  return (
    <TooltipProvider>
      <div
        data-slot="reaction-bar"
        className={cn(
          "flex flex-wrap items-center gap-x-1 gap-y-4",
          className,
        )}
        {...props}
      >
        {items
          .filter((item) => item.count > 0)
          .map((item) => (
            <ReactionItem
              key={item.id}
              item={item}
              onPressedChange={onPressedChange}
            />
          ))}

        <Popover open={isPickerOpen} onOpenChange={handlePickerOpenChange}>
          <Tooltip
            open={isAddTooltipOpen && !isPickerOpen}
            onOpenChange={(open) => {
              if (open && isAddTooltipSuppressed.current) return
              setIsAddTooltipOpen(open)
            }}
          >
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={addPickerLabel}
                  disabled={!hasAvailablePickerItem}
                  className="group relative ml-2 size-7 p-0 hover:bg-transparent before:absolute before:-inset-2 before:content-[''] focus-visible:border-transparent focus-visible:ring-0"
                  onPointerEnter={() => {
                    isAddTooltipSuppressed.current = false
                  }}
                  onBlur={() => {
                    isAddTooltipSuppressed.current = false
                    setIsAddTooltipOpen(false)
                  }}
                >
                  <span className="inline-flex size-7 items-center justify-center rounded-sm border border-input-border bg-background text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground group-focus-visible:border-ring group-focus-visible:ring-[3px] group-focus-visible:ring-ring/40">
                    <Icon name="smile-plus" color="inherit" size="sm" />
                  </span>
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent role="tooltip">{addPickerLabel}</TooltipContent>
          </Tooltip>

          <PopoverContent
            align="start"
            aria-label={addPickerLabel}
            className="w-auto min-w-0 max-w-[var(--radix-popover-content-available-width)] overflow-x-auto p-0"
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              const nextIndex =
                focusedPickerIndex >= 0
                  ? focusedPickerIndex
                  : (findEnabledPickerIndexes()[0] ?? -1)
              setFocusedPickerIndex(nextIndex)
              pickerButtonRefs.current[nextIndex]?.focus()
            }}
          >
            <div className="flex w-max items-center">
              {items.map((item, index) => (
                <Button
                  key={item.id}
                  ref={(node) => {
                    pickerButtonRefs.current[index] = node
                  }}
                  type="button"
                  variant="ghost"
                  aria-label={item.addLabel}
                  aria-pressed={item.pressed}
                  disabled={isPickerItemDisabled(item)}
                  tabIndex={index === focusedPickerIndex ? 0 : -1}
                  className="size-11 p-0 text-lg focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:ring-offset-1"
                  onClick={() => handlePickerSelection(item)}
                  onKeyDown={(event) => handlePickerKeyDown(event, index)}
                >
                  <span aria-hidden className="leading-none">
                    {item.symbol}
                  </span>
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  )
}

function ReactionItem({
  item,
  onPressedChange,
}: {
  item: ReactionBarItem
  onPressedChange: ReactionBarProps["onPressedChange"]
}) {
  const countId = React.useId()
  const hasActorPreview = Boolean(
    item.actorPreviewLabel &&
      ((item.actors?.length ?? 0) > 0 || item.moreActorsContent != null),
  )

  const handlePressedChange = (pressed: boolean) => {
    onPressedChange(item.id, pressed)
  }

  const toggle = (
    <Toggle
      type="button"
      variant="outline"
      pressed={item.pressed}
      disabled={item.disabled || item.pending}
      aria-label={item.pressed ? item.removeLabel : item.addLabel}
      aria-describedby={countId}
      aria-busy={item.pending || undefined}
      className={cn(
        "group relative h-7 min-w-11 border-0 bg-transparent p-0 hover:bg-transparent before:absolute before:-inset-y-2 before:inset-x-0 before:content-[''] focus-visible:border-transparent focus-visible:ring-0",
      )}
      onPressedChange={handlePressedChange}
    >
      <span
        className={cn(
          "inline-flex h-7 min-w-10 items-center justify-center gap-1 rounded-sm border border-border bg-background px-1.5 text-xs text-foreground transition-colors",
          "group-hover:bg-muted",
          "group-focus-visible:border-ring group-focus-visible:ring-[3px] group-focus-visible:ring-ring/40",
          "group-data-[state=on]:border-primary/40 group-data-[state=on]:bg-primary/5 group-data-[state=on]:text-primary",
          "dark:group-data-[state=on]:border-primary/60 dark:group-data-[state=on]:bg-primary/15 dark:group-data-[state=on]:text-primary",
        )}
      >
        <span aria-hidden className="text-base leading-none">
          {item.symbol}
        </span>
        <span id={countId} className="tabular-nums">
          {item.count}
        </span>
      </span>
    </Toggle>
  )

  if (!hasActorPreview) return toggle

  return (
    <Tooltip delayDuration={350}>
      <TooltipTrigger asChild>{toggle}</TooltipTrigger>
      <TooltipContent
        role="tooltip"
        side="bottom"
        sideOffset={6}
        aria-label={item.actorPreviewLabel}
        className="max-h-48 w-64 overflow-y-auto overscroll-contain bg-popover p-2 text-popover-foreground duration-150 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:animate-none motion-reduce:transition-none"
      >
        <div className="flex flex-col gap-1">
          {item.actors?.map((actor) => (
            <div key={actor.id}>{actor.content}</div>
          ))}
          {item.moreActorsContent != null ? (
            <p className="text-xs text-muted-foreground">
              {item.moreActorsContent}
            </p>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

export { ReactionBar }
